import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { createRouter } from 'vue-router';
import { RouteNames } from '@/models/enums/RouteNames';
import { routerOptions } from '@/routes';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { Role } from '@sysarb/sysarb-types/service-base/enums';
import { getUserTypeStartPageRoute } from './composables/useRouterHelper';
import { usePackage } from './composables/usePackage';
import { useOpentelemetry } from './composables/useOpentelemetry';
import type { Span } from '@opentelemetry/api';
import { activateIfUpdatePending } from './composables/useServiceWorkerUpdate';

/** Composables */
export const router = createRouter(routerOptions);

/** OpenTelemetry navigation tracking */
let navigationSpan: Span | null = null;
let navigationStartTime: number | null = null;

const isAsset = (to: RouteLocationNormalized, next: NavigationGuardNext): boolean => {
    if (to.fullPath.includes('assets')) {
        next();
        return true;
    }

    return false;
};

const isPublic = (to: RouteLocationNormalized, next: NavigationGuardNext): boolean => {
    const notPublic = to.meta?.requiresAuth || (to.meta?.userTypes ?? []).length > 0;

    if (!notPublic) {
        next();
        return true;
    }

    return false;
};

const hasNotCorrectRole = (isAuthenticated: boolean | undefined, role: Role | null | undefined, to: RouteLocationNormalized, next: NavigationGuardNext): boolean => {
    if (role == null) return false;
    if (isAuthenticated !== true) return false;
    if (to.meta?.requiresAuth !== true) return false;
    if (to.meta?.userTypes == null) return false;

    if (to?.meta?.userTypes.some(r => r === role) !== true) {
        // User has not required role
        const { role: currentRole } = useLoginStore();
        const packages = usePackage();
        const userRoute = getUserTypeStartPageRoute(currentRole?.role ?? null, packages);
        next(userRoute);

        console.info({ cat: 'router', msg: 'Role not allowed' });
        return true;
    }

    return false;
};

const hasNotCorrectLoginState = (isAuthenticated: boolean | undefined, to: RouteLocationNormalized, next: NavigationGuardNext): boolean => {
    if (to.meta.requiresAuth && isAuthenticated !== true) {
        next({
            name: RouteNames.Login,
            query: {
                url: to.fullPath
            }
        });

        console.info({ cat: 'router', msg: 'Not authenticated' });
        return true;
    }

    return false;
};

function checkPredefinedInstance(to: RouteLocationNormalized, _next: NavigationGuardNext): boolean {
    if (to.query.instance != null) {
        useLoginStore().setInstance(to.query.instance.toString());
        return true;
    }

    return false;
}

const hasNotVerifiedEmail = (isAuthenticated: boolean | undefined, emailVerified: boolean | undefined, isSimulating: boolean | undefined, to: RouteLocationNormalized, next: NavigationGuardNext): boolean => {
    if (isAuthenticated !== true) return false;
    if (to.meta?.requiresAuth !== true) return false;
    if (emailVerified !== false) return false;
    if (to.name === RouteNames.VerifyEmail) return false;

    // Ignore if the user is being simulated
    if (isSimulating === true) return false;

    next({
        name: RouteNames.VerifyEmail
    });

    return true;
};

router.beforeEach((to, from, next): void => {
    console.info({ cat: 'router', msg: `Navigating to: ${to.fullPath}`});

    // Start OpenTelemetry navigation span
    const { getTracer } = useOpentelemetry();
    const tracer = getTracer();
    navigationSpan = tracer.startSpan('router.navigation');
    navigationSpan.setAttribute('route.from', from.fullPath);
    navigationSpan.setAttribute('route.to', to.fullPath);
    navigationSpan.setAttribute('route.name', String(to.name ?? 'unknown'));
    navigationStartTime = performance.now();

    checkPredefinedInstance(to, next);

    const { userId, selectedInstance, emailVerified, isSimulating } = useLoginStore();

    const role = selectedInstance?.user?.roles?.find(r => r.isActive);
    const isAuthenticated = userId != null;

    if (isAuthenticated === true) {
        if (window.Intercom != null) {
            window.Intercom('update', { last_request_at: ((new Date()).getTime() / 1000) });
        }
    }

    if (isAsset(to, next)) return;
    if (isPublic(to, next)) return;

    if (hasNotCorrectLoginState(isAuthenticated, to, next)) return;
    if (hasNotCorrectRole(isAuthenticated, role?.role, to, next)) return;
    if (hasNotVerifiedEmail(isAuthenticated, emailVerified, isSimulating, to, next)) return;

    // Activate pending SW update:
    // - Hotfix releases: activate on any route change
    // - Normal releases: activate only on login transition (non-auth → auth route)
    const isLoginTransition = from.meta?.requiresAuth !== true;
    if (activateIfUpdatePending(to.fullPath, isLoginTransition)) return;

    next();
});

router.afterEach((_to, _from, failure): void => {
    // End OpenTelemetry navigation span
    if (navigationSpan != null) {
        if (failure != null) {
            navigationSpan.setAttribute('route.failure', true);
            navigationSpan.setAttribute('route.failure_type', failure.type);
        } else {
            navigationSpan.setAttribute('route.failure', false);
        }
        if (navigationStartTime != null) {
            navigationSpan.setAttribute('route.duration_ms', performance.now() - navigationStartTime);
        }
        navigationSpan.end();
        navigationSpan = null;
        navigationStartTime = null;
    }
});
